import { useEffect, useState } from "react";
import { Modal, Button, Select, message } from "antd";

//modules
import useTranslation from "../../hooks/TranslationContext";
import { apiCall } from "../../helpers/apiHelper";
import { selectDestination, setDestinationResolver } from "../../stores/userStore";
import { useAppDispatch } from "../../hooks/storeHooks";

//apis
import * as DestinationResolverApi from '../../apis/destinationResolverApi';

//models
import { DestinationResolverModel } from "../../models/destinationResolver";
import { ApiResponse } from "../../models/response";
import { User } from "../../models/auth";

// defines
type Props = {
  open: boolean;
  closeOnSave?: boolean;
  onCancel: () => void;
  onSave: (user?: User) => void;
};

const { Option } = Select;

export default function BranchPickerModal({ open, closeOnSave = false, onCancel, onSave }: Props) {
  const { t } = useTranslation();
    const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [datasource, setDatasource] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const abortController = new AbortController();

  useEffect(() => {
    getDataAsync();
  }, [open]);

  const getData = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DestinationResolverApi.getDestinationResolvers(abortSignal));

    let data = response.success ? DestinationResolverModel.toArrayOfClass((response.data?.value || [])) : [];

    if (data.length) {
      const isSameCompanyName = data.every(item => item.companyName === data[0].companyName);

      data = data.map((x: DestinationResolverModel) => {
        return {
          ...x,
          displayName: isSameCompanyName ? x.branchName : `${x.companyName}-${x.branchName}`
        }
      })
    }

    return data;
  };

  const getDataAsync = async () => {
    setLoading(true);
    setDatasource(await getData(abortController.signal));
    setLoading(false);
  }

  const onSelect = (value: any, option: any) => {
    setSelectedItem(value)
  }

  const onSubmit = async () => {
    if (!selectedItem) {
      return;
    }
    setLoading(true);
    const response: ApiResponse = await dispatch(selectDestination(selectedItem)).unwrap();
    setLoading(false);

    if (response.success) {
      const user = User.toClass(response.data?.value);

      dispatch(setDestinationResolver(datasource.find(x => x.id === selectedItem)));

      onSave?.(user);

      if (closeOnSave) {
        onCancel?.();
      }
    } else {
      message.error(response.errors.length > 0 ? `${response.errors[0]} ${response.errors[1]}` : response.errors[0]);
    }
  };

  return (
    <Modal
      title={t("select-branch")}
      open={open}
      onCancel={onCancel}
      footer={null}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium">{t("branch")}</label>
          <Select className="w-full" placeholder={t("select-branch")} onSelect={onSelect} loading={loading}>
            {
              datasource.map(item => <Option value={item.id} key={item.id.toString()}>{item.displayName}</Option>)
            }
          </Select>
        </div>
        <div className="flex justify-end space-x-2 gap-2">
          <Button onClick={onCancel}>{t("cancel")}</Button>
          <Button disabled={!selectedItem} type="primary" onClick={onSubmit}>
            {t("submit")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
